'use strict';

const _ = require('lodash');

const ConditionsList = require('./Conditions/List');
const ActionFactory = require('./Actions/Factory');

/**
 * Objeto de definición de una regla
 * @typedef {object} RuleDefinition
 *
 * @property {Number}                   id         Identificador único de la regla en la lista general
 * @property {Number}                   context    ID de la lista de contexto de la regla, o null si el contexto es
 *                                                 global
 * @property {ConditionsListDefinition} conditions Definición de las condiciones
 * @property {ActionDefinition[]}       actions    Definición de las acciones
 * @property {Structure.Rules}          parent     Lista que incluye esta regla
 */

/**
 * Formato de exportación de la regla para ser aplicada
 * @typedef {object} ExportedRule
 *
 * @property {Number}                 id         Identificador único de la regla en la lista general
 * @property {Number}                 context    ID de la lista de contexto de la regla, o null si el contexto es global
 * @property {ExportedConditionsList} conditions Condiciones exportadas
 * @property {ExportedAction[]}       actions    Acciones exportadas
 */

/**
 * Objeto que contiene la información de una regla y métodos para manejarla
 *
 * @property {RuleDefinition}           definition El objeto original almacenado en la base de datos
 * @property {Structure.ConditionsList} conditions Objeto de gestión de condiciones
 * @property {Structure.Action[]}       actions    Lista de objetos de acción que ejecuta la regla
 *
 * @memberOf Structure
 */
class Rule {
    /**
     * Factory
     *
     * @param  {...any} args Args
     *
     * @returns {Rule}  Rule instance
     *
     * @static
     */
    static instance(...args) {
        return new this(...args);
    }

    /**
     * Constructor de la clase, instancia las condiciones y las acciones de la regla
     *
     * @param  {RuleDefinition} rawRule Objeto de definición de la regla
     */
    constructor(rawRule = {}) {
        const { id, name = '', context, actions = [] } = rawRule;
        this.id = id;
        this.name = name;
        this.context = context;

        this.conditions = new ConditionsList(rawRule.conditions);
        this.conditions.setParent(this);

        this.actions = [];
        this.actionsByType = {};
        this.actionsById = {};
        actions.forEach(rawAction => {
            const action = ActionFactory.instantiateAction(rawAction);
            action.setRule(this);

            this.actions.push(action);

            this.actionsByType[action.getType()] = this.actionsByType[action.getType()] || [];
            this.actionsByType[action.getType()].push(action);

            this.actionsById[action.getId()] = action;
        });

        this.parent = null;

        this.disabled = !!rawRule.disabled;
    }

    /**
     * Obtiene una definición de la regla con un formato conocido para su aplicación
     *
     * @return {ExportedRule} El objeto generado
     */
    export() {
        return {
            id: this.getId(),
            ...this.getName() ? { name: this.getName() } : {},
            context: this.getContext(),
            conditions: this.getConditions().export(),
            actions: this.getActions().map(action => action.export()),
        };
    }

    /**
     * Obtiene el identificador interno de la regla
     *
     * @return {Number}  ID único numérico
     */
    getId() {
        return this.id;
    }
    /**
     * Obtiene el nombre de la regla
     *
     * @return {String} Nombre
     */
    getName() {
        return this.name;
    }

    /**
     * Asigna la lista padre a la que pertenece esta regla
     *
     * @param {Structure.Rules} parentList Objeto global de reglas
     */
    setParent(parentList) {
        this.parent = parentList;
    }

    /**
     * Determina si alguna de las acciones definidas en la regla tiene como objetivo el elemento con el ID seleccionado
     *
     * @param  {Number}   targetId ID único del elemento
     *
     * @return {boolean}           Si alguna acción afecta al elemento
     */
    hasActionTarget(targetId) {
        return _.some(this.actions, action => action.hasTarget(targetId));
    }

    /**
     * Determina si alguna de las acciones definidas en la regla tiene algún objetivo con uno de los IDs seleccionados
     *
     * @param  {integer[]}  targetIds IDs de elementos
     *
     * @return {boolean}              Si alguna acción incluye alguno de los IDs
     */
    hasAnyActionTarget(targetIds) {
        return _.some(this.actions, action => action.hasAnyTarget(targetIds));
    }

    /**
     * Determina si alguna de las condiciones de la regla incluye al elemento del CRF seleccionado
     *
     * @param  {Number}  elementId ID único del elemento
     *
     * @return {boolean}           Si alguna condición incluye al elemento
     */
    hasElementInConditions(elementId) {
        return this.conditions.hasElement(elementId);
    }

    /**
     * Determina si la regla tiene alguna acción de tipo fórmula donde esté incluido el ID del elemento seleccionado
     *
     * @param  {Number}  elementId ID único del elemento
     *
     * @return {boolean}           Si alguna fórmula contiene el elemento
     */
    hasElementInFormulas(elementId) {
        return _.some(this.actions, action => {
            return action.getType() === 'setValue' && action.hasFormula() && action.hasElementInFormula(elementId);
        });
    }

    /**
     * Determina si alguna de las acciones definidas en la regla es del tipo especificado
     *
     * @param  {string}  type Identificador del tipo de acción
     *
     * @return {boolean}      Si alguna acción es del tipo
     */
    hasActionType(type) {
        return this.actionsByType[type] && !!this.actionsByType[type].length;
    }

    /**
     * Obtiene la lista de acciones definidas en la regla
     *
     * @return {Structure.Action[]} Lista de acciones
     */
    getActions() {
        return this.actions;
    }

    /**
     * Obtiene la lista de condiciones definidas en la regla
     *
     * @return {Structure.ConditionsList} Objeto de tipo Lista de condiciones
     */
    getConditions() {
        return this.conditions;
    }

    /**
     * Obtiene la lista de condiciones individuales, es decir, excluye las listas de condiciones
     *
     * @return {Structure.Condition[]} Condiciones de la regla
     */
    getSingleConditions() {
        return this.getConditions().flattenConditions();
    }

    /**
     * Obtiene la lista de los campos que están incluidos en las condiciones de la regla
     *
     * @return {Structure.Field[]} Lista de campos
     */
    getFieldsInConditions() {
        return this.conditions.getFields();
    }

    /**
     * Obtiene la lista de los formularios que contienen los campos incluidos en las condiciones de la regla
     *
     * @return {Structure.Form[]} Lista de formularios
     */
    getFormsInConditions() {
        const forms = new Map();
        this.getFieldsInConditions().forEach(field => {
            const form = field.getForm();
            form && forms.set(form.getId(), form);
        });

        return Array.from(forms.values());
    }

    /**
     * Obtiene la lista de los campos que están incluidos en las fórmulas de las acciones de la regla
     *
     * @return {Structure.Field[]} Lista de campos
     */
    getFieldsInFormulas() {
        const fields = this.getActions()
            .reduce((acc, action) => {
                if (action.getType() === 'setValue' && action.hasFormula()) {
                    (action.fieldsInFormula || []).forEach(fieldId => {
                        const field = this.getCRF().getElement(fieldId);
                        field && acc.set(field.getId(), field);
                    });
                }

                return acc;
            }, new Map());

        return Array.from(fields.values());
    }

    /**
     * Obtiene la lista de las listas que están incluidas en las condiciones de la regla
     *
     * @return {Array<integer>} Lista de ID de listas
     */
    getListsInConditions() {
        return this.conditions.getLists();
    }

    /**
     * Obtiene la lista de las listas que están implicadas en las acciones de la regla, es decir, que algún campo
     * de la acción se encuentra bajo esa lista
     *
     * @return {Array<integer>} Lista de ID de listas
     */
    getListsInActions() {
        const crf = this.getCRF();

        return _.chain(this.actions)
            .map(action => {
                if (action.target) {
                    const element = crf.getElement(action.target);
                    if (element) {
                        return element.getParentLists();
                    }
                }
            })
            .flatten()
            .compact()
            .value();
    }

    /**
     * Obtiene la referencia al objeto global de CRF
     *
     * @return {Structure.CRF} Objeto de gestión del estudio
     */
    getCRF() {
        if (!this.parent) {
            return null;
        }

        return this.parent.getCRF();
    }

    /**
     * Obtiene el ID de la lista de contexto de ejecución de la regla (null si es de contexto global)
     *
     * @return {Number}  el identificador numérico de la lista
     */
    getContext() {
        return this.context;
    }

    /**
     * Obtiene la definición de las listas que intervienen en el contexto de la regla
     * Si la regla tiene un contexto de lista, el resultado es la propia lista junto con sus listas de nivel superior
     *
     * @return {Structure.List[]} Lista de listas de contexto
     */
    getContextLists() {
        const listId = this.getContext();
        if (!listId) {
            return [];
        }

        const crf = this.getCRF();
        if (!crf) {
            return [];
        }

        const list = crf.getList(listId);
        if (!list) {
            return [];
        }

        return [...list.getParentLists(), list];
    }

    /**
     * Obtiene una acción definida en la regla
     *
     * @param  {string}           actionId ID de la acción
     *
     * @return {Structure.Action}          La acción solicitada
     */
    getAction(actionId) {
        return this.actionsById[actionId] || null;
    }

    /**
     * Determina si la regla contiene la acción seleccionada
     *
     * @param  {string}  actionId ID de la acción
     *
     * @return {boolean}          Si existe en esta regla
     */
    hasAction(actionId) {
        return !!this.actionsById[actionId];
    }

    /**
     * Obtiene los elementos del CRF que son destinatarios de acciones de la regla
     *
     * @return {Structure.Container[]} Lista de elementos
     */
    getActionTargets() {
        return _(this.getActions())
            .map(_.method('getTarget'))
            .uniqBy()
            .filter()
            .value();
    }

    /**
     * Obtiene la relación de campos que aparecen como destinatarios de las acciones de la regla
     *
     * @return {Structure.Field[]} Lista de instancias de campo
     */
    getFieldsInActions() {
        return this.getActionTargets().filter(_.method('isField'));
    }

    /**
     * Determina si la regla se puede encadenar. Esto significa que el resultado de alguna de sus acciones puede a su
     * vez desencadenar otra regla; por ejemplo, si establece el valor de un campo ese mismo campo puede entrar en las
     * condiciones de otra regla que habría que encadenar
     *
     * @return {boolean} Si alguna de las acciones de la regla tiene potencial para servir de entrada a otra regla
     */
    isChainable() {
        return _.some(this.getActions(), action => {
            // Las acciones de tipo "hideChildren" restablecen el valor de las variables que se ocultan
            return _.includes(['setValue', 'hideChildren'], action.getType());
        });
    }

    /**
     * Determina si en la regla hay condiciones de comparación con un valor de aleatorización
     *
     * @return {boolean} Si alguna de las condiciones implica la aleatorización del registro
     */
    hasRandomization() {
        return this.getConditions().hasRandomization();
    }

    /**
     * Obtiene los identificadores de campos del registro que intervienen en las condiciones de esta regla
     *
     * @return {RecordFields[]} Lista de identificadores de campos
     */
    getRecordFieldsInConditions() {
        return this.getConditions().getRecordFields();
    }

    /**
     * Determina si la regla está deshabilitada
     *
     * @return {boolean} Si está deshabilitada
     */
    isDisabled() {
        return this.disabled;
    }

    /**
     * Determina si la regla está habilitada
     *
     * @return {boolean} Si está habilitada (no está deshabilitada)
     */
    isEnabled() {
        return !this.isDisabled();
    }

    /**
     * Marca el flag de deshabilitado
     *
     * @return {Action} El objeto de regla modificado
     */
    disable() {
        this.disabled = true;

        return this;
    }

    /**
     * Desmarca el flag de deshabilitado
     *
     * @return {Action} El objeto de regla modificado
     */
    enable() {
        this.disabled = false;

        return this;
    }
}

module.exports = Rule;
